import React from "react";

// Constants for random string generation
const RANDOM_BASE = 36;
const RANDOM_STRING_START = 2;
const RANDOM_STRING_LENGTH = 9;

// Constants for modal z-index management
const MODAL_ZINDEX_BASE = 10500;
const MODAL_BACKDROP_ZINDEX_BASE = 10499;

// Constants for modal z-index fix delays
const MODAL_ZINDEX_FIX_DELAY_MS = 100;
const MODAL_ZINDEX_FIX_DELAY_LONG_MS = 300;

/*
 * Global cleanup handler for MediaLibraryModal backdrop issues
 * This ensures backdrops are properly removed when the modal closes
 * Works even if modal is added dynamically after page load
 */
if (
  typeof window !== "undefined" &&
  !window._mediaLibraryBackdropCleanupInitialized
) {
  window._mediaLibraryBackdropCleanupInitialized = true;

  // Cleanup function to remove all backdrops and restore body state
  function cleanupBackdrop() {
    // Remove all modal backdrops (Bootstrap may create multiple)
    const backdrops = document.querySelectorAll(".modal-backdrop");
    backdrops.forEach((backdrop) => {
      backdrop.remove();
    });

    // Remove modal-open class from body
    document.body.classList.remove("modal-open");

    // Reset body padding if it was set
    const bodyStyle = window.getComputedStyle(document.body);
    if (bodyStyle.paddingRight) {
      document.body.style.paddingRight = "";
    }

    // Remove overflow hidden if set
    document.body.style.overflow = "";
  }

  // Store handler function for native event listeners (needed for removeEventListener)
  let nativeEventHandler = null;

  // Function to attach cleanup handler to MediaLibraryModal
  function attachCleanupHandler() {
    const mediaLibraryModal = document.getElementById("MediaLibraryModal");
    if (mediaLibraryModal && !mediaLibraryModal.dataset.cleanupAttached) {
      mediaLibraryModal.dataset.cleanupAttached = "true";

      // Use jQuery if available, otherwise use native events
      if (window.$ && window.$.fn.modal) {
        const $modal = window.$(mediaLibraryModal);
        // Remove any existing handlers first to prevent duplicates
        $modal.off("hidden.bs.modal.mediaLibraryCleanup");
        // Attach with namespace so we can remove it later if needed
        $modal.on("hidden.bs.modal.mediaLibraryCleanup", function () {
          // Cleanup immediately and after delays to ensure Bootstrap has finished
          cleanupBackdrop();
          setTimeout(cleanupBackdrop, MODAL_ZINDEX_FIX_DELAY_MS);
          setTimeout(cleanupBackdrop, MODAL_ZINDEX_FIX_DELAY_LONG_MS);
        });
      } else {
        /*
         * Fallback for native event listeners
         * Create handler function once and reuse it
         */
        if (!nativeEventHandler) {
          nativeEventHandler = function () {
            cleanupBackdrop();
            setTimeout(cleanupBackdrop, MODAL_ZINDEX_FIX_DELAY_MS);
            setTimeout(cleanupBackdrop, MODAL_ZINDEX_FIX_DELAY_LONG_MS);
          };
        }
        mediaLibraryModal.removeEventListener(
          "hidden.bs.modal",
          nativeEventHandler
        );
        mediaLibraryModal.addEventListener(
          "hidden.bs.modal",
          nativeEventHandler,
          { once: false }
        );
      }
    }
  }

  // Expose attachCleanupHandler globally so it can be called when modal is shown
  window._attachMediaLibraryCleanup = attachCleanupHandler;

  // Try to attach immediately if modal already exists
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", attachCleanupHandler);
  } else {
    attachCleanupHandler();
  }

  // Also try attaching when modal is shown (in case it's added dynamically)
  document.addEventListener("shown.bs.modal", function (e) {
    if (e.target && e.target.id === "MediaLibraryModal") {
      attachCleanupHandler();
    }
  });
}

// Custom field component for media selection
export function MediaField({ field, onChange, value }) {
  const [imageUrl, setImageUrl] = React.useState(value || "");
  const fieldId = React.useRef(
    `media-field-${Math.random()
      .toString(RANDOM_BASE)
      .substr(RANDOM_STRING_START, RANDOM_STRING_LENGTH)}`
  ).current;

  React.useEffect(() => {
    setImageUrl(value || "");
  }, [value]);

  function handleSelectImage() {
    // Check if we have a media library instance (either mediaLibraryInstance or mediaLibrary)
    const mediaLib = window.mediaLibraryInstance || window.mediaLibrary;

    if (mediaLib && mediaLib.$modal) {
      /*
       * Set up the callback
       * Note: MediaLibrary.js already calls modal('hide') before invoking this callback,
       * so we should NOT call it again here to avoid double-hiding which can break Bootstrap cleanup
       */
      mediaLib.imageSelectedCallback = (imageData) => {
        const url =
          imageData.imageUrl ||
          (imageData.imageId ? `/img/${imageData.imageId}` : "") ||
          imageData.url ||
          "";
        if (url) {
          setImageUrl(url);
          onChange(url);
        }
        // MediaLibrary already closes the modal, so we don't need to do it here
      };

      // Ensure cleanup handler is attached (in case modal was just added)
      if (
        window._mediaLibraryBackdropCleanupInitialized &&
        typeof window._attachMediaLibraryCleanup === "function"
      ) {
        window._attachMediaLibraryCleanup();
      }

      // Show the modal
      mediaLib.$modal.modal("show");

      // Ensure modal appears above Puck interface (z-index fix)
      function fixModalZIndex() {
        const modalElement = mediaLib.$modal[0];
        const backdrops = document.querySelectorAll(".modal-backdrop");

        if (modalElement) {
          modalElement.style.zIndex = MODAL_ZINDEX_BASE.toString();
          modalElement.style.position = "fixed";
        }
        // Fix all backdrops (Bootstrap may create multiple)
        backdrops.forEach((backdrop, index) => {
          backdrop.style.zIndex = (
            MODAL_BACKDROP_ZINDEX_BASE - index
          ).toString();
        });
      }

      // Fix immediately and after a short delay (Bootstrap creates backdrop asynchronously)
      fixModalZIndex();
      setTimeout(fixModalZIndex, MODAL_ZINDEX_FIX_DELAY_MS);

      // Listen for Bootstrap modal events to fix z-index when shown
      const modalElement = mediaLib.$modal[0];
      if (modalElement) {
        function handleShown() {
          fixModalZIndex();
        }
        modalElement.addEventListener("shown.bs.modal", handleShown);

        // Clean up listener when modal is hidden
        modalElement.addEventListener(
          "hidden.bs.modal",
          () => {
            modalElement.removeEventListener("shown.bs.modal", handleShown);
          },
          { once: true }
        );
      }
    } else if (window.$ && window.$("#MediaLibraryModal").length) {
      // Fallback: try to open media library modal directly
      const modal = window.$("#MediaLibraryModal");

      /*
       * Set up a temporary callback
       * Note: This fallback path manually handles image selection, so we need to close the modal ourselves
       */
      window.tempMediaLibraryCallback = (imageData) => {
        const url =
          imageData.imageUrl ||
          (imageData.imageId ? `/img/${imageData.imageId}` : "") ||
          imageData.url ||
          "";
        if (url) {
          setImageUrl(url);
          onChange(url);
        }
        window.tempMediaLibraryCallback = null;
      };

      // Override the click handler temporarily
      modal
        .find(".flex-images")
        .off("click.puckselect")
        .on("click.puckselect", ".item", function (e) {
          if ($(e.target).closest(".archive-image, .edit-image").length > 0)
            return;

          const $item = $(this);
          const imageData = {
            imageId: $item.data("image-id") || $item.data("imageId"),
            imageUrl: $item.find("img").attr("src"),
            width: $item.data("width"),
            height: $item.data("height"),
            altText: $item.data("alt-text") || ""
          };

          if (window.tempMediaLibraryCallback) {
            window.tempMediaLibraryCallback(imageData);
            // Close modal - Bootstrap should handle backdrop cleanup
            modal.modal("hide");
          }
        });

      modal.modal("show");

      // Ensure modal appears above Puck interface (z-index fix)
      function fixModalZIndex() {
        const modalElement = modal[0];
        const backdrops = document.querySelectorAll(".modal-backdrop");

        if (modalElement) {
          modalElement.style.zIndex = MODAL_ZINDEX_BASE.toString();
          modalElement.style.position = "fixed";
        }
        // Fix all backdrops (Bootstrap may create multiple)
        backdrops.forEach((backdrop, index) => {
          backdrop.style.zIndex = (
            MODAL_BACKDROP_ZINDEX_BASE - index
          ).toString();
        });
      }

      // Fix immediately and after a short delay (Bootstrap creates backdrop asynchronously)
      fixModalZIndex();
      setTimeout(fixModalZIndex, MODAL_ZINDEX_FIX_DELAY_MS);

      // Listen for Bootstrap modal events to fix z-index when shown
      const modalElement = modal[0];
      if (modalElement) {
        function handleShown() {
          fixModalZIndex();
        }
        modalElement.addEventListener("shown.bs.modal", handleShown);

        // Clean up listener when modal is hidden
        modalElement.addEventListener(
          "hidden.bs.modal",
          () => {
            modalElement.removeEventListener("shown.bs.modal", handleShown);
          },
          { once: true }
        );
      }
    } else {
      // Final fallback: simple prompt
      const url = prompt("Enter image URL:");
      if (url) {
        setImageUrl(url);
        onChange(url);
      }
    }
  }

  function handleClear() {
    setImageUrl("");
    onChange("");
  }

  return React.createElement(
    "div",
    { className: "puck-media-field" },
    React.createElement(
      "label",
      { className: "puck-field-label" },
      field.label || "Image"
    ),
    React.createElement(
      "div",
      { className: "media-field-preview mb-2" },
      imageUrl
        ? React.createElement("img", {
            src: imageUrl,
            alt: "Selected image",
            style: { maxWidth: "200px", maxHeight: "150px", display: "block" }
          })
        : React.createElement(
            "p",
            { className: "text-muted mb-0" },
            "No image selected"
          )
    ),
    React.createElement(
      "div",
      { className: "media-field-controls" },
      React.createElement("input", {
        type: "text",
        id: fieldId,
        value: imageUrl,
        className: "form-control mb-2",
        placeholder: "Image URL",
        readOnly: true
      }),
      React.createElement(
        "div",
        { className: "btn-group" },
        React.createElement(
          "button",
          {
            type: "button",
            className: "btn btn-secondary btn-sm",
            onClick: handleSelectImage
          },
          React.createElement("i", { className: "fas fa-image me-1" }),
          " Select Image"
        ),
        imageUrl &&
          React.createElement(
            "button",
            {
              type: "button",
              className: "btn btn-outline-secondary btn-sm",
              onClick: handleClear
            },
            React.createElement("i", { className: "fas fa-times" }),
            " Clear"
          )
      )
    )
  );
}
